import { useState, useEffect, useCallback, useContext, Fragment, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { calculateTotalPay, calculateTotalDeposit } from '../utils/proposalCalculations';

// Use unified date utils
import { moment } from '../utils/dateUtils';

import {
  Box,
  Button,
  Chip,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';

// Icons
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import BuildIcon from '@mui/icons-material/Build';
import HandymanIcon from '@mui/icons-material/Handyman';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Icons for the Completion
import ConstructionIcon from '@mui/icons-material/Construction';
import HouseIcon from '@mui/icons-material/House';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PercentIcon from '@mui/icons-material/Percent';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

// Custom Components
import { Spinner, SearchBox, ProposalDrawer, BrowserTitle } from '../components';

// Utils
import messages from '../static/messages';
import { AuthContext } from '../context/AuthProvider';

// Services
import proposalsServices from '../services/proposalsServices';
import userServices from '../services/userServices';

// Hooks
import useSearch from '../hooks/useSearch';
import usePagination from '../hooks/usePagination';
import useDrawer from '../hooks/useDrawer';
import useResizableColumns from '../hooks/useResizableColumns';

// Styles
import {
  table_header_with_search,
  table_container_base,
  check_for_deleted_row,
  actions_column,
  flex_center,
} from '../static/styles';

import { button } from '../static/theme-styles';

// Compact "client opened the link" indicator (eye icon + distinct-day count).
const ClientViewsBadge = ({ proposal, sx }) => {
  const count = proposal?.viewCount || 0;
  const last = proposal?.lastViewedAt;
  const title = count > 0
    ? `Client opened the link on ${count} day${count === 1 ? '' : 's'}${last ? ` · last: ${moment(last).format('MM/DD/YYYY h:mm A')}` : ''}`
    : 'Client has not opened the link yet';
  return (
    <Tooltip title={title} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          fontSize: 12,
          fontWeight: 700,
          color: count > 0 ? '#2e7d32' : '#bdbdbd',
          ...sx,
        }}
      >
        <VisibilityIcon sx={{ fontSize: 15 }} />
        {count}
      </Box>
    </Tooltip>
  );
};

// External variables
const base_proposal = {
  status: 'Active',
  proposalType: 'Installation',
  property: {
    id: '',
  },
  emailTo: [''],
  client: {
    id: '',
  },
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function fillYearsArray() {
  const currentYear = new Date().getFullYear();
  const endYear = 2016;
  const yearsArr = [];

  for (let year = currentYear; year >= endYear; year--) {
    yearsArr.push(year);
  }

  return yearsArr;
}

const statuses = ['Active', 'Pending', 'Cold', 'Cancelled', 'Completed', 'Lost', 'Paid', 'Unpaid'];


const getPaymentSortValue = (proposal) => {
  const payments = Array.isArray(proposal?.payments) ? proposal.payments : [];
  if (!payments.length) {
    // No money received yet — sort by the expected payment method note, if any.
    return (proposal?.additionalNotes?.payment?.expectedMethod || '').toLowerCase();
  }

  return payments
    .map((payment) => `${payment?.methodName || ''} ${payment?.reference || ''}`.trim())
    .join(' | ')
    .toLowerCase();
};

const resizeHandleSx = {
  position: 'absolute', right: 0, top: 0, bottom: 0, width: 6,
  cursor: 'col-resize', zIndex: 1,
  '&:hover, &:active': { backgroundColor: 'primary.main', opacity: 0.3 },
};

const ResizableTH = ({ width, onResize, colIndex, children, sx = {}, ...props }) => (
  <TableCell
    {...props}
    sx={{ position: 'relative', width, minWidth: 20, overflow: 'hidden', whiteSpace: 'nowrap', ...sx }}
  >
    {children}
    {onResize && <Box sx={resizeHandleSx} onMouseDown={(e) => onResize(colIndex, e)} />}
  </TableCell>
);

export const Proposals = () => {
  const { userData } = useContext(AuthContext);

  const [proposalsList, setProposalsList] = useState();
  const [techniciansList, setTechniciansList] = useState();
  const [showDeleted, setShowDeleted] = useState(false);
  const [showOnlyDeleted, setShowOnlyDeleted] = useState(false);
  const [totalCount, setTotalCount] = useState();
  const [currentProposal, setCurrentProposal] = useState();
  const [isNewProposal, setProposalMode] = useState(false);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bump this to force the data-loading useEffect to refetch the current view
  // (e.g. after a delete/restore mutation). Keeps refetch logic centralized
  // instead of duplicating each branch in every handler.
  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  const DEFAULT_COL_WIDTHS = useMemo(() => [57, 176, 418, 196, 185, 104, 687, 151, 113, 101, 154], []);
  const { percentages: colPct, onMouseDown: onColResize, resetWidths: resetColWidths } = useResizableColumns('proposalsColWidths', DEFAULT_COL_WIDTHS);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Load saved filters from localStorage or use defaults
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('proposalsFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that saved values are still valid
        // Use explicit checks to allow empty string values for "All"
        return {
          user: parsed.user !== undefined ? parsed.user : 'all',
          year: parsed.year !== undefined ? parsed.year : currentYear,
          month: parsed.month !== undefined ? parsed.month : currentMonth,
          status: parsed.status !== undefined ? parsed.status : 'all',
          proposalType: parsed.proposalType !== undefined ? parsed.proposalType : 'all',
        };
      }
    } catch (e) {
      console.log('Error loading saved filters:', e);
    }
    return {
      user: 'all',
      year: currentYear,
      month: currentMonth,
      status: 'all',
      proposalType: 'all',
    };
  };

  const [filters, setFilters] = useState(getSavedFilters);
  const [filtersApplied, setFiltersApplied] = useState(() => {
    // Check if we have saved filters to auto-apply
    try {
      const saved = localStorage.getItem('proposalsFilters');
      return !!saved;
    } catch (e) {
      return false;
    }
  });
  const [switchStatus, setSwitchStatus] = useState(false);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  // Columns the backend sorts server-side across the WHOLE filtered set
  // (whitelisted / mapped to SQL expressions in
  // api/src/repository/ProposalRepository.ts). For these we send
  // `sort=field,dir`; the computed ones (date, payment, deposit, total) are
  // ordered via SQL expressions there, so we must NOT re-sort in memory (that
  // would only reorder the current page out of many).
  const SERVER_SORTABLE_FIELDS = useMemo(
    () => new Set(['publicId', 'createdAt', 'status', 'date', 'payment', 'deposit', 'total']),
    [],
  );
  const serverSort = useMemo(
    () => (SERVER_SORTABLE_FIELDS.has(orderBy) ? `${orderBy},${order}` : 'createdAt,desc'),
    [orderBy, order, SERVER_SORTABLE_FIELDS],
  );

  // List state (query/page/per_page) is mirrored into the URL (see the
  // replaceState effect below), so coming back from a proposal — via the
  // "Back to Proposals" button or the browser Back button — restores the
  // search results instead of resetting the list.
  const initialUrlParams = new URLSearchParams(window.location.search);
  const { searchQuery, setSearchQuery, isSearching, searchRequest } = useSearch(
    proposalsServices.searchRequest,
    initialUrlParams.get('query') || '',
  );
  // Read saved rows-per-page BEFORE hook so useState initializes with correct value (no double-fetch)
  const savedRpp = parseInt(localStorage.getItem('proposalsRowsPerPage'), 10);
  const urlRpp = parseInt(initialUrlParams.get('per_page'), 10);
  const urlPage = parseInt(initialUrlParams.get('page'), 10);
  const initialRpp = [25, 50, 100, 200].includes(urlRpp)
    ? urlRpp
    : savedRpp && [25, 50, 100, 200].includes(savedRpp) ? savedRpp : 100;
  const { rowsPerPage, setRowsPerPage, page, setPage, handleChangePage, handleChangeRowsPerPage } = usePagination(
    initialRpp,
    Number.isInteger(urlPage) && urlPage > 0 ? urlPage : 0,
  );
  const { drawer, openDrawer, foolCheck } = useDrawer(currentProposal);

  const confirm = useConfirm();
  const theme = useTheme();
  const smallScreen = useMediaQuery('(max-width: 1440px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const years = fillYearsArray();
  
  // Action functions
  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();

    if (confirmed === true) {
      setCurrentProposal(null);
      setProposalMode(false);
      resetErrors && resetErrors();
    }
  };

  const onAddProposal = () => {
    setProposalMode(true);
    setCurrentProposal(base_proposal);
    openDrawer();
  };
  
  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = new Set(sortedAndFilteredProposals.filter(p => !p.deletedAt).map(p => p.id));
      setSelectedIds(newSelected);
    } else {
      setSelectedIds(new Set());
    }
  };
  
  const handleSelectOne = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const MAX_PRINT_PROPOSALS = 50;
  
  const handlePrintSelected = () => {
    if (selectedIds.size === 0) {
      toast.warning('Please select at least one proposal to print');
      return;
    }
    
    if (selectedIds.size > MAX_PRINT_PROPOSALS) {
      toast.error(`Maximum ${MAX_PRINT_PROPOSALS} proposals can be printed at once. You selected ${selectedIds.size}.`);
      return;
    }
    
    const ids = Array.from(selectedIds).join(',');
    window.open(`/proposals/print-batch?ids=${ids}`, '_blank');
  };

  // Handler functions
  const handleRemove = (id) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Proposal'),
    })
      .then(() => removeProposal(id))
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleRestore = (id) => {
    confirm({
      title: messages.confirm_message.restore,
      description: messages.confirm_message.restore_description('Proposal'),
    })
      .then(() => restoreProposal(id))
      .catch(() => toast.info(messages.cancel_message));
  };

  const handleFilterChange = (event) => {
    const key = event.target.name;
    const val = event.target.value;

    setFilters((prevState) => ({
      ...prevState,
      [key]: val,
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handlers below only update state. The data-loading useEffect below reacts
  // to filtersApplied/showOnlyDeleted/searchQuery/page/rowsPerPage/reloadKey
  // and issues exactly one server-side request per state transition.
  //
  // NOTE: triggerReload() is required in places where the visible UI intent
  // changes (e.g. user picked a new status dropdown and clicked Find) but
  // none of the useEffect deps would change otherwise — the new filters live
  // in localStorage, which the effect reads, not in state it subscribes to.
  const handleApplyFilters = () => {
    setShowOnlyDeleted(false);
    setSelectedIds(new Set());
    setPage(0);
    localStorage.setItem('proposalsFilters', JSON.stringify(filters));
    setFiltersApplied(true);
    setSwitchStatus(true);
    triggerReload();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      user: 'all',
      year: currentYear,
      month: currentMonth,
      status: 'all',
      proposalType: 'all',
    };
    setFilters(defaultFilters);
    localStorage.removeItem('proposalsFilters');
    setFiltersApplied(false);
    setShowOnlyDeleted(false);
    setSwitchStatus(false);
    setSelectedIds(new Set());
    setPage(0);
    triggerReload();
  };

  const handleShowDeleted = () => {
    setSelectedIds(new Set());
    setPage(0);
    if (showOnlyDeleted) {
      setShowOnlyDeleted(false);
      // filtersApplied keeps its prior value; useEffect will reload data
      // (either filtered list or default list) accordingly.
    } else {
      setShowOnlyDeleted(true);
      setFiltersApplied(false); // hide reset button while viewing deleted
      setSwitchStatus(true);
    }
  };

  // Sort the current server-side page in memory for columns that the
  // backend can't sort cheaply (computed/joined values: payment, deposit,
  // total contract price, installation date). For SERVER_SORTABLE_FIELDS
  // the rows already come back from the API in the right order — touching
  // them again here would just reorder one page out of many. See the
  // useEffect above that feeds `serverSort` into the data-loading calls.
  const sortedAndFilteredProposals = useMemo(() => {
    if (!proposalsList) return [];

    if (SERVER_SORTABLE_FIELDS.has(orderBy)) {
      return proposalsList;
    }

    return [...proposalsList].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'date':
          aValue = a.nextInstallation?.date ? new Date(a.nextInstallation.date).getTime() : 0;
          bValue = b.nextInstallation?.date ? new Date(b.nextInstallation.date).getTime() : 0;
          break;
        case 'payment':
          aValue = getPaymentSortValue(a);
          bValue = getPaymentSortValue(b);
          break;
        case 'deposit':
          aValue = calculateTotalDeposit(a) || 0;
          bValue = calculateTotalDeposit(b) || 0;
          break;
        case 'total':
          aValue = calculateTotalPay(a) || 0;
          bValue = calculateTotalPay(b) || 0;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [proposalsList, orderBy, order, SERVER_SORTABLE_FIELDS]);

  // Pagination is fully server-side now; the in-memory list IS one page.
  const paginatedProposals = sortedAndFilteredProposals;

  // Calculate selection state for header checkbox
  const selectableProposals = sortedAndFilteredProposals.filter(p => !p.deletedAt);
  const numSelected = selectedIds.size;
  const numSelectable = selectableProposals.length;
  const isAllSelected = numSelectable > 0 && numSelected === numSelectable;
  const isIndeterminate = numSelected > 0 && numSelected < numSelectable;

  // Request counter to prevent stale responses from overwriting fresh data
  const fetchIdRef = useRef(0);

  // Async functions
  const getProposalsList = async (skip, take, widthDeleted = true, sort = 'createdAt,desc') => {
    const thisId = ++fetchIdRef.current;
    try {
      const response = await proposalsServices.getAllProposals(skip, take, widthDeleted, sort);
      if (fetchIdRef.current !== thisId) return; // stale response
      setTotalCount(response.data.total);
      setProposalsList(response.data.data);
    } catch (error) {
      if (fetchIdRef.current !== thisId) return;
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const getSearchResult = useCallback(
    async (skip, take, sort) => {
      const thisId = ++fetchIdRef.current;
      const result = await searchRequest(skip, take, sort);
      if (fetchIdRef.current !== thisId) return; // stale response
      setTotalCount(result.total);
      setProposalsList(result.data);
    },
    [searchRequest],
  );

  const getTechniciansList = async () => {
    try {
      const response = await userServices.getAllUsers(0, 1000000, false);

      if (response.status === 206) setTechniciansList(response.data.data);
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const removeProposal = async (id) => {
    try {
      const response = await proposalsServices.remove(id);

      if (response.status === 200) {
        toast.success(messages.proposal.remove);
        triggerReload();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const restoreProposal = async (id) => {
    try {
      const response = await proposalsServices.restore(id);

      if (response.status === 200) {
        toast.success(messages.proposal.restore);
        triggerReload();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const createProposal = async () => {
    delete currentProposal.isEdited;

    try {
      const response = await proposalsServices.create(currentProposal);

      if (response.status === 200) {
        onCloseDrawer();
        toast.success(messages.proposal.create);
        navigate(`/proposals/${response.data.id}`);
      }
    } catch (error) {
      if (error.response.status === 409) toast.error(error.response.data.message);
      else toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };


  // Persist rows per page preference
  const handleChangeRowsPerPageWithSave = (event) => {
    const value = +event.target.value;
    handleChangeRowsPerPage(event);
    localStorage.setItem('proposalsRowsPerPage', String(value));
  };

  useEffect(() => {
    // Mount-time work that doesn't depend on the data-loading effect below.
    // The data-loading effect handles the initial proposals fetch via its
    // own dependencies (filtersApplied/showOnlyDeleted/searchQuery are
    // already initialized from localStorage in their useState initializers).
    getTechniciansList();
  }, []);

  // Single data-loading effect. Picks one of four branches based on UI state:
  //   1. showOnlyDeleted → server-side pagination over deleted records.
  //   2. filtersApplied → server-side pagination using saved filters,
  //      with the free-text query passed alongside (combo search).
  //   3. searchQuery only → server-side pagination through search endpoint
  //      (uses useSearch's AbortController for cancel-on-typing).
  //   4. Nothing → server-side pagination over the full list.
  // Branches 1–2 manage their own stale-response guard via fetchIdRef;
  // branches 3–4 delegate to helpers that already implement that guard.
  useEffect(() => {
    // Branch 1: deleted-only, server-side
    if (showOnlyDeleted) {
      const thisId = ++fetchIdRef.current;
      proposalsServices
        .getDeletedProposals(page * rowsPerPage, rowsPerPage)
        .then((response) => {
          if (fetchIdRef.current !== thisId) return;
          setTotalCount(response.data.total);
          setProposalsList(response.data.data);
          setSwitchStatus(true);
        })
        .catch((error) => {
          if (fetchIdRef.current !== thisId) return;
          toast.error(messages.errors.error_data_loaning);
          console.log(error);
        });
      return;
    }

    // Branch 2: filters (with or without free-text query), server-side.
    // The backend ILIKEs publicId, address fields, client name, technician
    // name and price (see ProposalRepository.findAndCountPageable). The
    // combo filters+query case is therefore one round trip.
    if (filtersApplied) {
      const saved = JSON.parse(localStorage.getItem('proposalsFilters') || '{}');
      const thisId = ++fetchIdRef.current;
      proposalsServices
        .searchByYearMonth(
          saved.year, saved.month, saved.status, saved.user, saved.proposalType,
          page * rowsPerPage, rowsPerPage, searchQuery, serverSort,
        )
        .then((response) => {
          if (fetchIdRef.current !== thisId) return;
          setTotalCount(response.data.total);
          setProposalsList(response.data.data);
          setSwitchStatus(true);
        })
        .catch((error) => {
          if (fetchIdRef.current !== thisId) return;
          toast.error(messages.errors.error_data_loaning);
          console.log(error);
        });
      return;
    }

    // Branch 3: free-text search only, server-side via /proposal?query=…
    if (searchQuery) {
      getSearchResult(page * rowsPerPage, rowsPerPage, serverSort);
      setSwitchStatus(true);
      return;
    }

    // Branch 4: default list, server-side
    getProposalsList(page * rowsPerPage, rowsPerPage, false, serverSort);
    setSwitchStatus(false);
  }, [searchQuery, getSearchResult, page, rowsPerPage, filtersApplied, showOnlyDeleted, reloadKey, serverSort]);

  // Restore list state when the router lands on /proposals with params
  // (e.g. the "Back to Proposals" button). Mount-time restore is handled
  // by the useState initializers above; this covers in-place navigations.
  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const p = parseInt(params.get('page'), 10);
    const rpp = parseInt(params.get('per_page'), 10);
    const q = params.get('query');
    if (Number.isInteger(p) && p >= 0) setPage(p);
    if ([25, 50, 100, 200].includes(rpp)) setRowsPerPage(rpp);
    if (q !== null) setSearchQuery(q);
  }, [location.search]);

  // Mirror query/page/per_page into the URL without triggering a router
  // navigation (replaceState), so the history entry for this list always
  // carries the current state and the browser Back button restores it.
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('query', searchQuery);
    if (page > 0) params.set('page', String(page));
    params.set('per_page', String(rowsPerPage));
    window.history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
  }, [searchQuery, page, rowsPerPage, location.pathname]);
  
  // Clear selection when page changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, rowsPerPage]);

  // Check for data loading
  if (!proposalsList) return <Spinner />;

  return (
    <Box component="section">
      <BrowserTitle title="Proposals | Airmaxx Pro" />
      <Box
        sx={(theme) => ({
          ...table_header_with_search,
          [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
          },
        })}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" endIcon={<AddIcon />} onClick={onAddProposal} sx={button('primary', 'secondary')}>
            Add new
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            onClick={handlePrintSelected}
            disabled={selectedIds.size === 0}
            sx={{
              ...button('primary'),
              backgroundColor: selectedIds.size > 0 ? '#1976d2' : '#ccc',
              '&:hover': {
                backgroundColor: selectedIds.size > 0 ? '#1565c0' : '#ccc',
              },
            }}
          >
            Print Selected ({selectedIds.size})
          </Button>
        </Box>

        <Box
          sx={(theme) => ({
            ...flex_center,
            maxWidth: smallScreen ? '70vw' : '60vw',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
              flexWrap: 'wrap',
              rowGap: '15px',
              mt: 2,
            },
          })}
        >
          <FormControl fullWidth sx={{ mr: 2 }} size="small">
            <InputLabel id="user">User</InputLabel>
            <Select labelId="user" label="User" value={filters.user} name="user" onChange={handleFilterChange}>
              <MenuItem value="all">All</MenuItem>
              {techniciansList?.map((el) => (
                <MenuItem value={el.id} key={el.id}>
                  {el.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mr: 2 }} size="small">
            <InputLabel id="year">Year</InputLabel>
            <Select labelId="year" label="Year" value={filters.year} name="year" onChange={handleFilterChange}>
              <MenuItem value="">All</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mr: 2 }} size="small">
            <InputLabel id="month">Month</InputLabel>
            <Select labelId="month" label="Month" value={filters.month} name="month" onChange={handleFilterChange}>
              <MenuItem value="">All</MenuItem>
              {months.map((month, index) => (
                <MenuItem key={index} value={index + 1}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mr: 2 }} size="small">
            <InputLabel id="status">Status</InputLabel>
            <Select labelId="status" label="Status" value={filters.status} name="status" onChange={handleFilterChange}>
              <MenuItem value="all">All</MenuItem>
              {statuses.map((el, index) => (
                <MenuItem key={index} value={el}>
                  {el}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mr: 2 }} size="small">
            <InputLabel id="proposalType">Type</InputLabel>
            <Select labelId="proposalType" label="Type" value={filters.proposalType} name="proposalType" onChange={handleFilterChange}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="Installation">HIC</MenuItem>
              <MenuItem value="Service">SVC</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              ...button('primary'),
              minWidth: '80px',
              mr: 1,
            }}
          >
            Find
          </Button>

          {filtersApplied && (
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{
                minWidth: '70px',
                mr: 1,
                color: '#666',
                borderColor: '#ccc',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
              }}
            >
              Reset
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleShowDeleted}
            sx={{
              ...button(showOnlyDeleted ? 'primary' : 'secondary'),
              minWidth: '140px',
            }}
          >
            {showOnlyDeleted ? 'Show Active' : 'Show Deleted'}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <SearchBox 
          searchRequest={searchRequest} 
          value={searchQuery} 
          setValue={setSearchQuery}
          placeholder="Search by ID, address, client name, $price or date (MM/DD)"
        />

        {/* Mobile Card View */}
        {isMobile && (
          <Box sx={{ p: 2 }}>
            {isSearching ? (
              <Spinner />
            ) : paginatedProposals.length ? (
              paginatedProposals.map((proposal) => (
                <Card 
                  key={proposal.id} 
                  sx={{ 
                    mb: 2, 
                    opacity: proposal.deletedAt ? 0.6 : 1,
                    border: proposal.signature ? '2px solid #4caf50' : proposal.readAt ? '2px solid #ff9800' : proposal.sentEmails?.length > 0 ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    {/* Header: ID + Status */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="subtitle1" 
                          component={NavLink}
                          to={`/proposals/view/${proposal.id}`}
                          sx={{ 
                            fontWeight: 600, 
                            color: '#1976d2',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            '&:active': { color: '#1565c0' }
                          }}
                        >
                          {proposal.publicId}
                        </Typography>
                        <ClientViewsBadge proposal={proposal} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          icon={proposal.proposalType === 'Service' ? <HandymanIcon /> : <BuildIcon />}
                          label={proposal.proposalType === 'Service' ? 'SVC' : 'HIC'}
                          size="small"
                          sx={{
                            backgroundColor: proposal.proposalType === 'Service' ? '#ff9800' : '#1976d2',
                            color: '#fff',
                            '& .MuiChip-icon': { color: '#fff' },
                          }}
                        />
                        <Chip
                          label={proposal.status}
                          size="small"
                          sx={{
                            backgroundColor: proposal.status === 'Active' ? '#d3d3d3' : 
                                            proposal.status === 'Pending' ? '#ffa500' : 
                                            proposal.status === 'Cold' ? '#42a5f5' :
                                            proposal.status === 'Completed' ? '#008000' :
                                            proposal.status === 'Lost' ? '#e91e63' : '#ff0000',
                            color: ['Pending', 'Completed', 'Cold', 'Lost'].includes(proposal.status) ? '#fff' : '#000',
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Address */}
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {proposal.property?.address || 'No address'}
                    </Typography>

                    {/* Total Price */}
                    <Typography variant="h6" sx={{ color: '#c62828', fontWeight: 700 }}>
                      {(() => {
                        const totalPay = calculateTotalPay(proposal);
                        return totalPay > 0
                          ? totalPay.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : '$0.00';
                      })()}
                    </Typography>

                    <Divider sx={{ my: 1 }} />

                    {/* Additional Info */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      {proposal.nextInstallation && (
                        <Typography variant="caption" color="text.secondary">
                          📅 {moment(proposal.nextInstallation.date?.split('T')[0], 'YYYY-MM-DD').format('MM/DD/YY')}
                        </Typography>
                      )}
                      <Completion data={proposal.additionalNotes} />
                    </Box>

                    {/* Contact History */}
                    {(() => {
                      const touches = (proposal.touches || [])
                        .slice()
                        .sort((a, b) => new Date(b.contactedAt) - new Date(a.contactedAt));
                      if (!touches.length) return null;
                      const latest = touches[0];
                      const text = latest.note || `${latest.touchType}${latest.outcome ? ` — ${latest.outcome}` : ''}`;
                      return (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic', backgroundColor: proposal.contactHistoryColor || 'transparent', borderRadius: 1, px: proposal.contactHistoryColor ? 1 : 0, py: proposal.contactHistoryColor ? 0.5 : 0 }}>
                          {latest.touchType}: {text.substring(0, 50)}{text.length > 50 ? '...' : ''}
                        </Typography>
                      );
                    })()}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pt: 0 }}>
                    {!proposal.deletedAt && (
                      <Checkbox
                        size="small"
                        checked={selectedIds.has(proposal.id)}
                        onChange={() => handleSelectOne(proposal.id)}
                      />
                    )}
                    <Box>
                      {proposal.deletedAt ? (
                        <IconButton size="small" onClick={() => handleRestore(proposal.id)} color="primary">
                          <SettingsBackupRestoreIcon />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton 
                            size="small" 
                            component={NavLink} 
                            to={`/proposals/${proposal.id}`}
                            state={{ page, rowsPerPage, query: searchQuery }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            component="a" 
                            href={`/proposals/view/${proposal.id}`} 
                            target="_blank"
                            sx={{ color: proposal.signature ? '#2e7d32' : proposal.readAt ? '#ff9800' : proposal.sentEmails?.length > 0 ? '#1976d2' : 'inherit' }}
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemove(proposal.id)} color="error">
                            <DeleteForeverIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                {searchQuery ? 'No proposals found' : 'No Data'}
              </Typography>
            )}
          </Box>
        )}

        {/* Desktop Table View */}
        {!isMobile && (
        <TableContainer sx={{ ...table_container_base, borderRadius: 2 }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'separate', borderSpacing: '0 2px' }}>
            <TableHead>
              <TableRow sx={{
                '& th': { backgroundColor: '#e8f5e9', fontWeight: 600, borderBottom: '2px solid #e0e0e0' },
                '& th:first-of-type': { borderTopLeftRadius: 8 },
                '& th:last-of-type': { borderTopRightRadius: 8 },
              }}>
                <ResizableTH width={colPct[0]} onResize={onColResize} colIndex={0} padding="none" sx={{ px: 0 }}>
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'select all proposals' }}
                    size="small"
                    sx={{ p: 0 }}
                  />
                </ResizableTH>
                <ResizableTH width={colPct[1]} onResize={onColResize} colIndex={1}>
                  <TableSortLabel
                    active={orderBy === 'publicId'}
                    direction={orderBy === 'publicId' ? order : 'asc'}
                    onClick={() => handleRequestSort('publicId')}
                  >
                    Proposal ID
                  </TableSortLabel>
                </ResizableTH>
                <ResizableTH width={colPct[2]} onResize={onColResize} colIndex={2}>Address</ResizableTH>
                <ResizableTH width={colPct[3]} onResize={onColResize} colIndex={3}>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => handleRequestSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </ResizableTH>
                <ResizableTH width={colPct[4]} onResize={onColResize} colIndex={4}>Completion</ResizableTH>
                <ResizableTH width={colPct[5]} onResize={onColResize} colIndex={5} sx={{ fontSize: 13 }}>
                  <TableSortLabel
                    active={orderBy === 'payment'}
                    direction={orderBy === 'payment' ? order : 'asc'}
                    onClick={() => handleRequestSort('payment')}
                  >
                    Payment
                  </TableSortLabel>
                </ResizableTH>
                <ResizableTH width={colPct[6]} onResize={onColResize} colIndex={6}>Contact History</ResizableTH>
                <ResizableTH width={colPct[7]} onResize={onColResize} colIndex={7}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </ResizableTH>
                <ResizableTH width={colPct[8]} onResize={onColResize} colIndex={8} sx={{ fontSize: 13 }}>
                  <TableSortLabel
                    active={orderBy === 'deposit'}
                    direction={orderBy === 'deposit' ? order : 'asc'}
                    onClick={() => handleRequestSort('deposit')}
                  >
                    Deposit
                  </TableSortLabel>
                </ResizableTH>
                <ResizableTH width={colPct[9]} onResize={onColResize} colIndex={9} sx={{ fontSize: 13 }}>
                  <TableSortLabel
                    active={orderBy === 'total'}
                    direction={orderBy === 'total' ? order : 'asc'}
                    onClick={() => handleRequestSort('total')}
                  >
                    Total
                  </TableSortLabel>
                </ResizableTH>
                <ResizableTH width={colPct[10]}>
                  Actions
                  <Tooltip title="Reset column widths">
                    <IconButton size="small" onClick={resetColWidths} sx={{ ml: 0.5, p: 0, opacity: 0.4, '&:hover': { opacity: 1 } }}>
                      <SettingsBackupRestoreIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </ResizableTH>
              </TableRow>
            </TableHead>
            <TableBody>
              {isSearching ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Spinner />
                  </TableCell>
                </TableRow>
              ) : (
                <Fragment>
                  {paginatedProposals.length ? (
                    paginatedProposals.map((proposal, idx) => (
                      <TableRow key={proposal.id} sx={{
                        ...check_for_deleted_row(!!proposal.deletedAt),
                        ...(!proposal.deletedAt && idx % 2 === 1 ? { backgroundColor: '#f0f4f8' } : {}),
                        '& td': { borderBottom: '1px solid #f0f0f0' },
                        '& td:first-of-type': { borderTopLeftRadius: 6, borderBottomLeftRadius: 6 },
                        '& td:last-of-type': { borderTopRightRadius: 6, borderBottomRightRadius: 6 },
                      }} hover>
                        <TableCell padding="none" sx={{ width: 24, minWidth: 24, maxWidth: 24, px: 0 }}>
                          {!proposal.deletedAt && (
                            <Checkbox
                              checked={selectedIds.has(proposal.id)}
                              onChange={() => handleSelectOne(proposal.id)}
                              inputProps={{ 'aria-label': `select proposal ${proposal.publicId}` }}
                              size="small"
                              sx={{ p: 0 }}
                            />
                          )}
                        </TableCell>
                        <TableCell scope="row" sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          <NavLink
                            className="full-col-flex-link"
                            to={`/proposals/view/${proposal.id}`}
                            onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                            style={{ fontSize: 15, fontWeight: 700 }}
                          >
                            <Tooltip title={proposal.proposalType === 'Service' ? 'Service (SVC)' : 'Installation (HIC)'}>
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: proposal.proposalType === 'Service' ? '#ff9800' : '#1976d2',
                                  mr: 0.5,
                                  verticalAlign: 'middle',
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                            {proposal.publicId}
                            <ClientViewsBadge proposal={proposal} sx={{ ml: 0.75 }} />
                          </NavLink>
                        </TableCell>
                        <TableCell sx={{ fontSize: 15 }}>
                          <NavLink
                            className="full-col-flex-link"
                            to={`/proposals/view/${proposal.id}`}
                            onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                          >
                            {proposal.property?.address || 'No address'}
                          </NavLink>
                        </TableCell>
                        <TableCell sx={{ fontSize: 15 }}>
                          <NavLink
                            className="full-col-flex-link"
                            to={`/proposals/view/${proposal.id}`}
                            onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                          >
                            {proposal.nextInstallation &&
                              `${moment(proposal.nextInstallation.date?.split('T')[0], 'YYYY-MM-DD').format('MM/DD/YYYY')} ${
                                proposal.nextInstallation.installationTime
                              }`}
                          </NavLink>
                        </TableCell>
                        <TableCell>
                          <Completion data={proposal.additionalNotes} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(() => {
                            const payments = proposal.payments || [];
                            if (payments.length === 0) {
                              // Nothing received yet: show the expected payment
                              // method (a note, not money) in orange.
                              const expected = proposal.additionalNotes?.payment?.expectedMethod;
                              if (!expected) return '-';
                              return (
                                <Tooltip title="Expected payment method — payment not received yet">
                                  <Box component="span" sx={{ display: 'block', lineHeight: 1.4, color: '#9e9e9e', fontWeight: 600 }}>
                                    {expected}
                                  </Box>
                                </Tooltip>
                              );
                            }
                            // grey = expectation, orange = received by tech /
                            // in flight (submitted, pending ACH), green = posted
                            return payments.map((p, i) => {
                              const unconfirmed = p.status === 'submitted' || p.status === 'pending';
                              const title = `${p.methodName || ''}${p.reference ? ` #${p.reference}` : ''}${p.amount ? ` — $${parseFloat(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}${p.status === 'submitted' ? ' — sent by tech, awaiting accounting' : p.status === 'pending' ? ' — pending' : ''}`;
                              return (
                                <Tooltip key={p.id || i} title={title}>
                                  <Box component="span" sx={{ display: 'block', lineHeight: 1.4, color: unconfirmed ? '#ed6c02' : '#2e7d32', fontWeight: unconfirmed ? 600 : 400 }}>
                                    {p.methodName || ''}{p.reference ? ` #${p.reference}` : ''}
                                  </Box>
                                </Tooltip>
                              );
                            });
                          })()}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', backgroundColor: proposal.contactHistoryColor || undefined }}>
                          <NavLink
                            className="full-col-flex-link"
                            to={`/proposals/view/${proposal.id}`}
                            onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                          >
                            {(() => {
                              const touches = (proposal.touches || [])
                                .slice()
                                .sort((a, b) => new Date(b.contactedAt) - new Date(a.contactedAt));
                              if (!touches.length) return <span style={{ color: '#aaa' }}>—</span>;
                              const latest = touches[0];
                              const tooltipContent = (
                                <Box sx={{ maxWidth: 360, maxHeight: 320, overflow: 'auto' }}>
                                  {touches.map((t, i) => (
                                    <Box key={t.id} sx={{ mb: i < touches.length - 1 ? 1.5 : 0, pb: i < touches.length - 1 ? 1.5 : 0, borderBottom: i < touches.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none' }}>
                                      <Box sx={{ fontWeight: 700, mb: 0.3 }}>{t.touchType}{t.outcome ? ` — ${t.outcome}` : ''}</Box>
                                      {t.nextFollowUpAt && <Box sx={{ fontSize: 11, opacity: 0.8 }}>Next: {new Date(t.nextFollowUpAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Box>}
                                      {t.note && <Box sx={{ fontSize: 12, mt: 0.3, whiteSpace: 'pre-wrap' }}>{t.note}</Box>}
                                      <Box sx={{ fontSize: 10, opacity: 0.6, mt: 0.3 }}>{new Date(t.contactedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Box>
                                    </Box>
                                  ))}
                                </Box>
                              );
                              return (
                                <Tooltip title={tooltipContent} arrow placement="left"
                                  componentsProps={{ tooltip: { sx: { maxWidth: 400, bgcolor: 'rgba(33,33,33,0.95)', p: 1.5, fontSize: 12 } } }}
                                >
                                  <span>{latest.note || `${latest.touchType}${latest.outcome ? ` — ${latest.outcome}` : ''}`}</span>
                                </Tooltip>
                              );
                            })()}
                          </NavLink>
                        </TableCell>
                        <TableCell>
                          <NavLink
                            className="full-col-flex-link"
                            to={`/proposals/view/${proposal.id}`}
                            onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                          >
                            <Chip
                              label={proposal.status}
                              sx={{
                                maxWidth: 100,
                                margin: '0 auto',
                                backgroundColor: () => {
                                  if (proposal.status === 'Active') return '#d3d3d3';
                                  else if (proposal.status === 'Pending') return '#ffa500';
                                  else if (proposal.status === 'Cold') return '#42a5f5';
                                  else if (proposal.status === 'Completed') return '#008000';
                                  else if (proposal.status === 'Lost') return '#e91e63';

                                  return '#ff0000';
                                },
                                color: () => {
                                  if (proposal.status === 'Pending') return '#fff';
                                  else if (proposal.status === 'Cold') return '#fff';
                                  else if (proposal.status === 'Completed') return '#fff';
                                  else if (proposal.status === 'Lost') return '#fff';

                                  return '#000000de';
                                },
                              }}
                            />
                          </NavLink>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(() => {
                            const deposit = calculateTotalDeposit(proposal);
                            return deposit > 0
                              ? deposit.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                })
                              : '-';
                          })()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <NavLink
                            className="full-col-flex-link"
                            to={`/proposals/view/${proposal.id}`}
                            onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                          >
                            {(() => {
                              const totalPay = calculateTotalPay(proposal);
                              return totalPay > 0
                                ? totalPay.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                  })
                                : 0;
                            })()}
                          </NavLink>
                        </TableCell>
                        <TableCell sx={actions_column}>
                          {!!proposal.deletedAt ? (
                            <Tooltip title="Restore Proposal">
                              <IconButton aria-label="restore" onClick={() => handleRestore(proposal.id)}>
                                <SettingsBackupRestoreIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Fragment>
                              <NavLink
                                className="full-col-flex-link"
                                to={`/proposals/${proposal.id}`}
                                onClick={!!proposal.deletedAt ? (e) => e.preventDefault() : null}
                                state={{
                                  page: page,
                                  rowsPerPage: rowsPerPage,
                                  query: searchQuery,
                                }}
                              >
                                <Tooltip title="Edit Proposal">
                                  <IconButton aria-label="edit">
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              </NavLink>

                              <a href={`/proposals/view/${proposal.id}`} target="_blank" rel="noReferrer">
                                <Tooltip title="See Proposal">
                                  <IconButton
                                    aria-label="pdf"
                                    sx={{
                                      color: proposal.signature
                                        ? '#2e7d32'
                                        : proposal.readAt
                                          ? '#ff9800'
                                          : proposal.sentEmails?.length > 0
                                            ? '#1976d2'
                                            : 'rgba(0, 0, 0, 0.54)',
                                    }}
                                  >
                                    <PictureAsPdfIcon />
                                  </IconButton>
                                </Tooltip>
                              </a>
                              <Tooltip title="Delete Proposal">
                                <IconButton aria-label="remove" onClick={() => handleRemove(proposal.id)}>
                                  <DeleteForeverIcon />
                                </IconButton>
                              </Tooltip>
                            </Fragment>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ textAlign: 'center' }}>
                        <strong>{searchQuery ? 'No proposals found' : 'No Data'}</strong>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 200]}
        component="div"
        count={totalCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPageWithSave}
        labelRowsPerPage="Proposals per page"
      />

      <ProposalDrawer
        drawer={drawer}
        onCloseDrawer={onCloseDrawer}
        currentProposal={currentProposal}
        setCurrentProposal={setCurrentProposal}
        isNewProposal={isNewProposal}
        createProposal={createProposal}
      />
    </Box>
  );
};

const Completion = ({ data }) => (
  <Box sx={{ display: 'flex', gap: 0.25 }}>
    {/* EQUIPMENT */}
    <ConstructionIcon
      sx={{
        width: 20,
        height: 20,
        color: data?.orderEquipmentCompleted?.completed
          ? 'green'
          : data?.orderEquipmentCompleted?.comment
          ? 'red'
          : 'rgba(0, 0, 0, 0.54)',
      }}
    />

    {/* TITLE 24 */}
    <HouseIcon
      sx={{
        width: 20,
        height: 20,
        color: data?.title24?.completed ? 'green' : data?.title24?.comment ? 'red' : 'rgba(0, 0, 0, 0.54)',
      }}
    />

    {/* PERMIT */}
    {data?.additionalNotes?.permit?.completed ? (
      <VpnKeyIcon sx={{ width: 20, height: 20, color: 'green' }} />
    ) : (
      <KeyOffIcon
        sx={{
          width: 20,
          height: 20,
          color: data?.permit?.comment ? 'red' : 'rgba(0, 0, 0, 0.87)',
        }}
      />
    )}

    {/* REBATE */}
    <PercentIcon
      sx={{
        width: 20,
        height: 20,
        color: data?.rebate?.completed ? 'green' : data?.rebate?.comment ? 'red' : 'rgba(0, 0, 0, 0.54)',
      }}
    />

    {/* PAYMENT */}
    {data?.payment?.completed ? (
      <MonetizationOnIcon sx={{ width: 20, height: 20, color: 'green' }} />
    ) : (
      <CurrencyExchangeIcon
        sx={{
          width: 20,
          height: 20,
          color: data?.payment?.comment ? 'red' : 'rgba(0, 0, 0, 0.87)',
        }}
      />
    )}

    {/* OTHER */}
    <AddIcon
      sx={{
        width: 20,
        height: 20,
        color: data?.other?.completed ? 'green' : data?.other?.comment ? 'red' : 'rgba(0, 0, 0, 0.54)',
      }}
    />
  </Box>
);
